import type { MessageSeverity } from '../../common/AppMessages';

// 202 = backend queued, ExportNotification picks it up; 200 = direct download.
//
// This module reports through an injected callback rather than calling native
// alert(). A native dialog blocks the renderer until a human dismisses it, which
// halts every automated run of the export path. onMessage is REQUIRED: an optional
// one would let a caller forget to wire it and silently swallow the reason an
// export failed.
export interface DownloadExportCsvArgs {
  url: string;
  filename: string;
  headers?: HeadersInit;
  onMessage: (text: string, severity: MessageSeverity) => void;
  /** Use the server's Content-Disposition filename (spatial downloads name their own zip/kml). */
  useServerFilename?: boolean;
}

export async function downloadExportCsv({ url, filename, headers, onMessage, useServerFilename }: DownloadExportCsvArgs): Promise<void> {
  let response: Response;
  try {
    response = await fetch(url, { credentials: 'include', headers });
  } catch {
    onMessage('Export request failed.', 'error');
    return;
  }

  if (response.status === 202) {
    const data = await response.json().catch(() => ({}));
    const count = typeof data?.count === 'number' ? data.count.toLocaleString() : 'a large number of';
    onMessage(
      `Your export of ${count} records is being prepared. You'll see a notification when it's ready to download.`,
      'info',
    );
    return;
  }
  if (response.status === 413) {
    onMessage((await response.json().catch(() => ({}))).message ?? 'Export too large.', 'error');
    return;
  }
  // An empty result is not a failure — the filters simply matched nothing.
  if (response.status === 404) {
    onMessage('No records match the current filters.', 'info');
    return;
  }
  // A refused request says why (e.g. an AFF tracks range over 31 days).
  if (response.status === 400) {
    onMessage((await response.json().catch(() => ({}))).message ?? 'Export failed.', 'error');
    return;
  }
  if (!response.ok) {
    onMessage('Export failed.', 'error');
    return;
  }

  const blob = await response.blob();
  const dateStamp = new Date().toISOString().slice(0, 10);
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  const serverName = useServerFilename
    ? /filename="([^"]+)"/.exec(response.headers.get('Content-Disposition') ?? '')?.[1]
    : undefined;
  a.download = serverName ?? `${filename}_${dateStamp}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(a.href);
}
